import { useCredits } from "hooks/useCredits";

interface CreditIndicatorProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
  onClick?: () => void;
}

/**
 * Returns the color for the credit indicator based on the percentage of credits remaining.
 * 90–100%: Green (#18B368)
 * 70–89%: Blue (#1570EF)
 * 50–69%: Orange (#EDA12F)
 * 0–49%: Red (#E74444)
 */
const getCreditColor = (credits: number, maxCredits: number): string => {
  const percentage = maxCredits > 0 ? (credits / maxCredits) * 100 : 0;
  if (percentage >= 90) return '#18B368'; // Green
  if (percentage >= 70) return '#1570EF'; // Blue
  if (percentage >= 50) return '#EDA12F'; // Orange
  return '#E74444'; // Red
};

const CreditIndicator: React.FC<CreditIndicatorProps> = ({
  size = 32,
  strokeWidth = 8,
  className = "",
  onClick,
}) => {
  const { credits: snapshot } = useCredits();

  // The ring measures everything spendable against the account's current
  // ceiling (weekly allowance + purchased), so a topped-up account never reads
  // as empty just because this week's grant is spent.
  const credits = snapshot?.totalAvailable ?? 0;
  const maxCredits = snapshot?.spendableCeiling ?? 0;
  const ringCredits = credits;
  const digitCount = credits.toString().length;

  const strokeColor = getCreditColor(ringCredits, maxCredits);

  // Determine font size based on digit count
  const getFontSize = () => {
    if (digitCount >= 5) return '8px';
    if (digitCount >= 4) return '10px';
    return '12px';
  };

  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = maxCredits > 0 ? Math.min((ringCredits / maxCredits) * circumference, circumference) : 0;

  return (
    <div
      className={`relative flex items-center justify-center ${onClick ? 'cursor-pointer' : ''} ${className}`}
      style={{ width: `${size}px`, height: `${size}px` }}
      title={`${credits.toLocaleString()} credits available (${ringCredits.toLocaleString()}/${maxCredits.toLocaleString()} spendable ceiling)`}
      onClick={onClick}
    >
      <svg
        className="absolute w-full h-full -rotate-90"
        viewBox={`0 0 ${size} ${size}`}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          className="transition-all duration-300"
        />
      </svg>
      <span
        className="font-Gelion font-semibold z-10 text-[#1D2939]"
        style={{ fontSize: getFontSize() }}
      >
        {credits}
      </span>
    </div>
  );
};

export { CreditIndicator, getCreditColor };
